import PropTypes from 'prop-types'
import { memo, useCallback, useContext, useEffect, useRef } from 'react'
import { TableContext } from 'components/table/Table'
import classNames from 'classnames'

const RowView = memo(
  ({
    className,
    checked,
    showCheckbox,
    isDisabled,
    onChange,
    onMouseEnter,
    onMouseLeave,
    children,
    ...attrs
  }) => (
    <tr
      className={className}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      {...attrs}>
      {showCheckbox && (
        <td className="align_c">
          <div className="inner_tbl">
            <label className={classNames('check_inp', { on: checked })}>
              <input
                type="checkbox"
                name="inpCheckSet"
                className="inp_check"
                checked={checked}
                disabled={isDisabled}
                onChange={onChange}
              />
              <span className="img_comm ico_check" />
            </label>
          </div>
        </td>
      )}
      {children}
    </tr>
  )
)

RowView.displayName = 'RowView'
RowView.propTypes = {
  className: PropTypes.string,
  checked: PropTypes.bool,
  showCheckbox: PropTypes.bool,
  isDisabled: PropTypes.bool,
  onChange: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  children: PropTypes.node,
}

const Row = memo(
  ({
    className,
    rowId,
    error = false,
    noCheckbox = false,
    isDisabled = false,
    hoverClassName = '',
    children,
    ...attrs
  }) => {
    const {
      hasCheckbox,
      rows,
      addRow,
      removeRow,
      toggleRow,
      hoveredId,
      setHoveredId,
    } = useContext(TableContext)

    const rowIdRef = useRef({ rowId })
    const row = rows[rowIdRef.current.rowId] || {}

    useEffect(() => {
      rowIdRef.current = addRow({
        id: rowId,
        noCheckbox,
        isDisabled,
      })
      return () => {
        removeRow(rowId)
      }
    }, [addRow, removeRow, rowId, noCheckbox, isDisabled])

    const isHovered = hoveredId === rowIdRef.current.rowId
    const onMouseEnter = useCallback(
      () => setHoveredId(rowIdRef.current.rowId),
      [setHoveredId]
    )
    const onMouseLeave = useCallback(() => {
      if (isHovered) setHoveredId(null)
    }, [isHovered, setHoveredId])
    const onChange = useCallback(
      () => toggleRow(rowIdRef.current.rowId),
      [toggleRow]
    )

    return (
      <RowView
        onChange={onChange}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        className={classNames(className, {
          error,
          on: row.checked,
          [hoverClassName]: isHovered,
        })}
        checked={!!row.checked}
        showCheckbox={hasCheckbox && !noCheckbox}
        isDisabled={isDisabled}
        {...attrs}>
        {children}
      </RowView>
    )
  }
)

export default Row

Row.displayName = 'Row'
Row.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  hoverClassName: PropTypes.string,
  rowId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  noCheckbox: PropTypes.bool,
  isDisabled: PropTypes.bool,
  error: PropTypes.any,
}
